import { useAuth } from '../contexts/AuthContext';
import { useCachedFlowCompletionStatus } from './useCachedFlowCompletionStatus';
import { INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';

/**
 * A custom hook that combines authentication state with intake assessment completion status
 * @returns Combined auth and intake status information
 */
export const useAuthWithIntakeStatus = () => {
  const {
    isAuthenticated,
    user,
    login,
    logout,
    updateUser,
    isAuthLoading,
    authExitReason,
    clearAuthExitReason,
  } = useAuth();
  const {
    completionStatus: intakeCompletionStatus,
    loading: intakeStatusLoading,
    error: intakeStatusError,
    refetch: refetchIntakeStatus,
  } = useCachedFlowCompletionStatus(
    INTAKE_ASSESSMENT_FLOW_NAME,
    true,
    isAuthenticated,
    user?.id != null ? String(user.id) : undefined,
    'system-intake',
  ); // Only fetch once authenticated and scoped per user

  const isIntakeStatusPending =
    isAuthenticated && !intakeStatusError && intakeCompletionStatus == null;
  const derivedIntakeStatusLoading = intakeStatusLoading || isIntakeStatusPending;

  // If we have an error and user data is available, use that as fallback
  const hasCompletedIntake = Boolean(
    intakeCompletionStatus?.completed_at ??
      user?.has_completed_intake ??
      false,
  );

  return {
    // Auth state
    isAuthenticated,
    user,
    isAuthLoading,
    authExitReason,
    clearAuthExitReason,
    login,
    logout,
    updateUser,

    // Intake status
    hasCompletedIntake,
    intakeCompletedAt: intakeCompletionStatus?.completed_at || null,
    intakeStatusLoading: derivedIntakeStatusLoading,
    intakeStatusError,
    refetchIntakeStatus,
  };
};

export default useAuthWithIntakeStatus;
